/* eslint-disable jsx-a11y/img-redundant-alt */
import React, {useCallback, useEffect, useRef, useState} from "react"
import {useNavigate, useParams} from "react-router-dom"
import {useTranslation} from "react-i18next"
import "../../css/Transitions.css"
import {Dialog, DialogContent} from "@mui/material"
import {CSSTransition} from "react-transition-group"
import {ReactComponent as ShareIcon} from "../../icon/share.svg"
import {ReactComponent as GalleryIcon} from "../../icon/gallery.svg"
import SecondaryIconButton from "../buttons/SecondaryIconButton"
import {useConfig} from "../../hooks/useConfig"
import {useBoothSession} from "../../hooks/useBoothSession"
import {fetchUpload, Gallery, GalleryPrivacy, ShareSheet, trackView, Upload, UploadLayout} from "@simplebooth/webkit"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined"
import MediaContainer from "../common/MediaContainer"
import BigButton from "../buttons/BigButton"
import {IosShareOutlined, OpenInNewOutlined} from "@mui/icons-material"
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined"

function PicPage() {
  const params = useParams<"code" | "uploadCode" | "picPresentation">()
  const uploadCode = params.uploadCode!
  const presentation = params.picPresentation ?? "view"
  const navigate = useNavigate()
  const {t} = useTranslation()

  const config = useConfig()
  const {clearImages} = useBoothSession()

  const resetBooth = useCallback(
    (fullReset: boolean) => {
      clearImages()
      if (fullReset) {
        navigate(`/${config.presetCode}`)
      } else {
        navigate(`/${config.presetCode}/camera`)
      }
    },
    [config, navigate, clearImages],
  )

  const [upload, setUpload] = useState<Upload | undefined>()
  const [gallery, setGallery] = useState<Gallery | undefined>()
  const [showShare, setShowShare] = useState(false)
  const [didShare, setDidShare] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const buttonPressTimer = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    fetchUpload(
      uploadCode,
      (uploadResource) => {
        const newUpload = Upload.fromResource(uploadResource)
        setUpload(newUpload)

        trackView(newUpload.code)

        if (uploadResource._embedded && uploadResource._embedded.gallery) {
          const newGallery = Gallery.fromResource(uploadResource._embedded.gallery)
          setGallery(newGallery)
        }
      },
      (errorCode: number) => {
        setError(`${errorCode} error`)
      },
    )
  }, [uploadCode])

  const theme = config.theme

  if (error) {
    return (
      <Stack alignItems="center" spacing={2}>
        <h3 style={{margin: "30px 0"}}>{error}</h3>
        <BigButton onClick={() => resetBooth(true)}>
          {t("pic.home")}
        </BigButton>
      </Stack>
    )
  }

  if (!upload || !gallery) {
    return <></>
  }

  if (upload.deleted) {
    return (
      <Stack alignItems="center" spacing={2}>
        <h3 style={{margin: "30px 0"}}>{t("pic.deleted")}</h3>
        <BigButton onClick={() => resetBooth(true)}>
          {t("pic.home")}
        </BigButton>
      </Stack>
    )
  }

  return (
    <>
      <CSSTransition
        in={config.finishedPicMessage != null && presentation === "new"}
        timeout={500}
        classNames="fade"
        unmountOnExit
      >
        <div className="basicContainer">
          <h3 style={{color: theme.textColor, marginTop: "20px"}}>{config.finishedPicMessage}</h3>
        </div>
      </CSSTransition>

      <MediaContainer>
        <img
          src={upload.media.original}
          alt="Your finished photo"
          onTouchStart={() => {
            buttonPressTimer.current = setTimeout(() => setDidShare(true), 1500)
          }}
          onTouchEnd={() => clearTimeout(buttonPressTimer.current)}
          style={{width: "100%", height: "100%"}}
        />
      </MediaContainer>

      <CSSTransition
        in={presentation === "new"}
        appear
        timeout={1000}
        classNames="fadeIn"
        unmountOnExit
      >
        <Stack alignItems="center">
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{mt: 3, width: "100%", px: 2}}>
            <BigButton variant="outlined" onClick={() => resetBooth(false)} sx={{flex: {xs: 1, sm: "none"}}}>
              <ReplayOutlinedIcon sx={{mr: 1}}/>
              {t("pic.takeAnother")}
            </BigButton>
            <BigButton onClick={() => setShowShare(true)} sx={{flex: {xs: 1, sm: "none"}}}>
              <IosShareOutlined sx={{mr: 1}}/>
              {config.buttons.share ? config.buttons.share.title : t("pic.share")}
            </BigButton>
          </Stack>

          {gallery.privacy === GalleryPrivacy.PUBLIC && gallery.links.webSelf && (
            <BigButton
              variant="text"
              size="small"
              onClick={() => {
                gallery?.links.webSelf && window.open(gallery.links.webSelf, "_blank")
              }}
              sx={{mt: 2}}
            >
              {t("closed.viewGallery")}
              <OpenInNewOutlined sx={{ml: 1}}/>
            </BigButton>
          )}
        </Stack>
      </CSSTransition>

      {presentation === "view" && (
        <Box sx={{mt: 3}}>
          {config.buttons.viewPic ? (
            <BigButton
              onClick={() => {
                if (config.buttons.viewPic && config.buttons.viewPic.url) {
                  window.location.href = config.buttons.viewPic.url
                }
              }}
            >
              {config.buttons.viewPic.title}
            </BigButton>
          ) : (
            <Stack direction="row" spacing={4} justifyContent="center" alignItems="center">
              {gallery.privacy === GalleryPrivacy.PUBLIC && gallery.links.webSelf && (
                <SecondaryIconButton
                  theme={theme}
                  text={t("pic.gallery")}
                  onPress={() => {
                    gallery?.links.webSelf && window.open(gallery.links.webSelf, "_blank")
                  }}
                >
                  <GalleryIcon style={theme.svgStyleAlternate}/>
                </SecondaryIconButton>
              )}

              <BigButton
                sx={{display: "flex", alignItems: "center", justifyContent: "center"}}
                onClick={() => resetBooth(true)}
              >
                <AutoAwesomeOutlinedIcon sx={{mr: 1}}/>
                {t("pic.participate")}
              </BigButton>

              <SecondaryIconButton
                theme={theme}
                text={t("pic.share")}
                onPress={() => setShowShare(true)}
              >
                <ShareIcon style={theme.svgStyleAlternate}/>
              </SecondaryIconButton>
            </Stack>
          )}
        </Box>
      )}

      <Dialog
        open={showShare}
        onClose={() => {
          setShowShare(false)
          setDidShare(true)
        }}
        slotProps={{ paper: { sx: { borderRadius: "0.7rem", maxWidth: 480 } } }}
      >
        <DialogContent sx={{ p: 4 }}>
          <ShareSheet
            oneColumn
            heading={
              upload.layout === UploadLayout.VIDEO
                ? t("shareSheet.headings.video")
                : t("shareSheet.headings.photo")
            }
            upload={upload}
            hashtags={gallery.hashtag}
            shareOptions={gallery.getShareOptions()}
            onHide={() => {
              setShowShare(false)
              setDidShare(true)
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default PicPage
